import React, { useEffect, useState } from 'react';
import { has, is } from '../../services';
import { Icon, IconName, IconSize } from '../icon';
import { Text } from '../text';
import { InputStatus, InputType } from './InputEnum';
import clsx from 'clsx';
import { hashClass } from '../../helpers';
import { useTrilogyContext } from '../../context';
import { getColorStyle, TrilogyColor } from '../../objects';
/**
 * Input Component
 * @param name {string} Input name
 * @param disabled {boolean} Disabled input
 * @param onChange {Function} OnChange Input Event
 * @param onFocus {Function} onFocus Input Event
 * @param onBlur {Function} onBlur Input Event
 * @param placeholder {string} Placeholder Input
 * @param type {InputType} Type for input
 * @param defaultValue {string} Default Value for Input
 * @param hasIcon {boolean} Adding if you want icon - Default icon is defined by status
 * @param customIcon {IconName} Adding if you want custom icon
 * @param status {InputStatus} Input with status - (SUCCESS|WARNING|ERROR|DEFAULT)
 * @param patternValidator {RegExp} regex validator
 * @param customValidator {Function} custom function validator
 * @param onStatusChange {Function} status change event
 * @param help {string} Help for input
 * @param ref Pass a ref for input
 * @param onSubmit {Function} onSubmit Event
 * @param maxLength {number} Textarea max length
 * @param customIconLeft {IconName} Adding if you want custom icon left
 * @param customIconRight {IconName} Adding if you want custom icon right
 * @param securityGauge {boolean} add security gauge for input type password
 * @param dataVerifies {IDataVerifies} Textarea max length

 * - -------------------------- WEB PROPERTIES -------------------------------
 * @param loading {boolean} Loading input
 * @param value {string} Value for Input
 * @param hovered {boolean} Hover mode
 * @param focused {boolean} Fucus mode
 * @param className {string} Additionnal CSS Classes
 * @param onMouseEnter {Function} onMouseEnter Input Event
 * @param onMouseLeave {Function} onMouseLeave Input Event
 * @param iconClassname {string} Additional icon classes
 * @param onKeyPress {Function} onKeyPress Input Event
 * @param onKeyUp {Function} onKeyUp Input Event
 * @param onIconClick {Function} onIconClick Input Event
 * @param onClick {Function} onClick Input Event
 * @param forceControl {boolean} Force the control of the input value
 * @param minLength {number} Textarea min length
 * @param accessibilityLabel {string} Accessibility label
 * @param testId {string} Test Id for Test Integration
 * - -------------------------- NATIVE PROPERTIES -------------------------------
 * @param autoCompleteType {InputAutoCompleteType} Auto complete input type
 */
const Input = ({ forceControl, className, disabled, onChange, onKeyPress, onKeyUp, onIconClick, onClick, onFocus, onBlur, patternValidator, onMouseEnter, onMouseLeave, name, placeholder, type = 'text', defaultValue, value, loading, hovered, focused, hasIcon, customIcon, status, help, iconClassname, reference, onStatusChange, customValidator, onSubmit, minLength, maxLength, testId, accessibilityLabel, autoCompleteType, customIconLeft, customIconRight, securityGauge, dataVerifies, ...others }) => {
    const { styled } = useTrilogyContext();
    const [_value, setValue] = useState(defaultValue !== null && defaultValue !== void 0 ? defaultValue : '');
    const [isHovered, setIsHovered] = useState(hovered !== null && hovered !== void 0 ? hovered : false);
    const [isFocused, setIsFocused] = useState(focused !== null && focused !== void 0 ? focused : false);
    const [isDirty, setIsDirty] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const [localStatus, setLocalStatus] = useState(status || InputStatus.DEFAULT);
    const validator = !customValidator && patternValidator
        ? (value) => (patternValidator.test(value) ? InputStatus.SUCCESS : InputStatus.ERROR)
        : customValidator;
    const [inputType, setInputType] = useState(type);
    const [isShowPwd, setIsShowPwd] = useState(false);
    useEffect(() => {
        var _a;
        setValue((_a = value !== null && value !== void 0 ? value : defaultValue) !== null && _a !== void 0 ? _a : '');
    }, [value, defaultValue]);
    useEffect(() => {
        setIsHovered(hovered !== null && hovered !== void 0 ? hovered : false);
    }, [hovered]);
    useEffect(() => {
        setIsFocused(focused !== null && focused !== void 0 ? focused : false);
    }, [focused]);
    useEffect(() => {
        if (isFocused) {
            setIsDirty(true);
        }
        else if (isDirty) {
            setIsTouched(true);
        }
    }, [isFocused, isDirty]);
    useEffect(() => {
        if (!validator || !isTouched) {
            return;
        }
        setLocalStatus(validator(_value));
    }, [isFocused, isTouched]);
    useEffect(() => {
        setLocalStatus(status || InputStatus.DEFAULT);
    }, [status]);
    useEffect(() => {
        if (onStatusChange) {
            onStatusChange(localStatus);
        }
    }, [localStatus]);
    const inputIcon = new Map();
    inputIcon.set(InputStatus.SUCCESS, IconName.CHECK_CIRCLE);
    inputIcon.set(InputStatus.WARNING, IconName.EXCLAMATION_CIRCLE);
    inputIcon.set(InputStatus.ERROR, IconName.EXCLAMATION_CIRCLE);
    const wrapperClasses = hashClass(styled, clsx('field', className, type === 'password' && securityGauge && 'has-gauge'));
    const hasPlaceholder = placeholder !== undefined && placeholder.length > 0;
    const iconTimesClasse = hashClass(styled, clsx('tri-times-circle'));
    const iconCheckClasse = hashClass(styled, clsx('tri-check-circle'));
    const successClasse = hashClass(styled, clsx('is-success'));
    const iconClasse = hashClass(styled, clsx('icon'));
    const controlClasses = hashClass(styled, clsx('control', hasPlaceholder && type !== InputType.SEARCH && has('dynamic-placeholder'), {
        [has('icons-right')]: hasIcon !== null && hasIcon !== void 0 ? hasIcon : (customIcon || customIconRight || type === 'password'),
        [is('disabled')]: disabled,
        ['has-icons-left']: customIconLeft || type === InputType.SEARCH,
    }));
    const classes = hashClass(styled, clsx('input', localStatus && is(localStatus), isHovered && !isFocused && is('hovered'), isFocused && !isHovered && is('focused')));
    const helpClasses = clsx('help', localStatus && is(localStatus));
    const refLengthVerify = React.useRef(null);
    const refSpecialCharsVerify = React.useRef(null);
    const refNumberVerify = React.useRef(null);
    const refUppercaseVerify = React.useRef(null);
    const refLowerercaseVerify = React.useRef(null);
    const [points, setPoints] = React.useState(0);
    const [nbAllVerifies, setNbAllVerifies] = React.useState(0);
    const [verifies, setVerifies] = React.useState({});
    const lengthVerify = {
        fn: (e) => {
            var _a, _b, _c, _d;
            if (((_a = dataVerifies === null || dataVerifies === void 0 ? void 0 : dataVerifies.length) === null || _a === void 0 ? void 0 : _a.max) && !dataVerifies.length.min) {
                return e.length > 0 && e.length <= dataVerifies.length.max;
            }
            if (((_b = dataVerifies === null || dataVerifies === void 0 ? void 0 : dataVerifies.length) === null || _b === void 0 ? void 0 : _b.min) && !((_c = dataVerifies === null || dataVerifies === void 0 ? void 0 : dataVerifies.length) === null || _c === void 0 ? void 0 : _c.max)) {
                return e.length >= dataVerifies.length.min;
            }
            if (((_d = dataVerifies === null || dataVerifies === void 0 ? void 0 : dataVerifies.length) === null || _d === void 0 ? void 0 : _d.max) && dataVerifies.length.min) {
                return e.length >= dataVerifies.length.min && e.length <= dataVerifies.length.max;
            }
            return false;
        },
        ref: refLengthVerify,
    };
    const specialCharsverify = {
        // eslint-disable-next-line
        fn: (e) => /[^\w\*]/.test(e),
        ref: refSpecialCharsVerify,
    };
    const numberVerify = {
        // eslint-disable-next-line
        fn: (e) => /[0-9]/.test(e),
        ref: refNumberVerify,
    };
    const uppercaseVerify = {
        // eslint-disable-next-line
        fn: (e) => /[A-Z]/.test(e),
        ref: refUppercaseVerify,
    };
    const lowercaseVerify = {
        // eslint-disable-next-line
        fn: (e) => /[a-z]/.test(e),
        ref: refLowerercaseVerify,
    };
    React.useEffect(() => {
        const data = {};
        dataVerifies &&
            Object.keys(dataVerifies).map((key) => {
                if (key === 'number')
                    Object.assign(data, { numberVerify });
                if (key === 'length')
                    Object.assign(data, { lengthVerify });
                if (key === 'lowercase')
                    Object.assign(data, { lowercaseVerify });
                if (key === 'uppercase')
                    Object.assign(data, { uppercaseVerify });
                if (key === 'specialChars')
                    Object.assign(data, { specialCharsverify });
            });
        setVerifies(data);
        setNbAllVerifies(Object.keys(data).length);
    }, [dataVerifies]);
    const handleVerifyPwd = (e) => {
        const verifiesTests = [];
        Object.keys(verifies).map((key) => {
            var _a, _b, _c, _d;
            const test = verifies[key].fn(e);
            verifiesTests.push(test);
            if (test) {
                (_a = verifies[key].ref.current) === null || _a === void 0 ? void 0 : _a.classList.remove(iconTimesClasse);
                (_b = verifies[key].ref.current) === null || _b === void 0 ? void 0 : _b.classList.add(iconCheckClasse, successClasse);
            }
            else {
                (_c = verifies[key].ref.current) === null || _c === void 0 ? void 0 : _c.classList.remove(iconCheckClasse, successClasse);
                (_d = verifies[key].ref.current) === null || _d === void 0 ? void 0 : _d.classList.add(iconTimesClasse);
            }
        });
        setPoints(verifiesTests.filter((item) => item).length);
    };
    const widthGauge = React.useMemo(() => {
        const calc = Number(((points / nbAllVerifies) * 100).toFixed(0));
        if (calc <= 50 && calc > 0)
            return '50%';
        if (calc <= 99 && calc > 50)
            return '75%';
        if (calc === 100)
            return '100%';
        return '0%';
    }, [points, nbAllVerifies]);
    const colorGauge = React.useMemo(() => {
        const calc = Number(((points / nbAllVerifies) * 100).toFixed(0));
        if (calc <= 50 && calc > 0)
            return getColorStyle(TrilogyColor.ERROR);
        if (calc <= 99 && calc > 50)
            return getColorStyle(TrilogyColor.WARNING);
        if (calc === 100)
            return getColorStyle(TrilogyColor.SUCCESS);
        return '#D1D1D1';
    }, [points, nbAllVerifies]);
    return (React.createElement("div", { className: wrapperClasses, "data-has-gauge": securityGauge ? true : undefined },
        React.createElement("div", { className: controlClasses },
            React.createElement("input", { role: 'textbox', ...others, "data-testid": testId, "aria-label": accessibilityLabel, type: inputType, className: classes, value: _value, defaultValue: defaultValue, name: name, onSubmit: onSubmit, ref: reference, disabled: disabled, minLength: minLength, maxLength: maxLength, autoComplete: autoCompleteType, onClick: (e) => {
                    const target = e.target;
                    if (onClick) {
                        onClick({
                            inputName: target.name,
                            inputValue: target.value,
                        });
                    }
                }, onKeyUp: (e) => {
                    const target = e.target;
                    if (onKeyUp) {
                        onKeyUp({
                            inputName: target.name,
                            inputValue: target.value,
                            inputKeyCode: e.keyCode,
                            inputEvent: e,
                        });
                    }
                }, onKeyPress: (e) => {
                    const target = e.target;
                    if (onKeyPress) {
                        onKeyPress({
                            inputName: target.name,
                            inputValue: target.value,
                            inputKeyCode: e.keyCode,
                            inputEvent: e,
                        });
                    }
                }, onChange: (e) => {
                    // --- solution to prevent cursor jump ---
                    if (inputType !== InputType.MONTH &&
                        inputType !== InputType.DATE &&
                        inputType !== InputType.DATETIME_LOCAL &&
                        inputType !== InputType.NUMBER &&
                        inputType !== InputType.EMAIL) {
                        const caret = e.target.selectionStart;
                        const element = e.target;
                        window.requestAnimationFrame(() => {
                            element.selectionStart = caret;
                            element.selectionEnd = caret;
                        });
                    }
                    // ---------------------------------------
                    // eslint-disable-next-line no-console
                    if (!forceControl)
                        setValue(e.target.value);
                    if (onChange) {
                        onChange({
                            inputName: e.target.name,
                            inputValue: e.target.value,
                            inputSelectionStart: e.target.selectionStart,
                            inputEvent: e,
                        });
                    }
                    if (type === 'password' && securityGauge) {
                        handleVerifyPwd(e.target.value);
                    }
                }, onFocus: (e) => {
                    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
                    setIsFocused(true);
                }, onBlur: (e) => {
                    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
                    setIsFocused(false);
                }, onMouseEnter: (e) => {
                    onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(e);
                    setIsHovered(true);
                }, onMouseLeave: (e) => {
                    onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(e);
                    setIsHovered(false);
                }, placeholder: placeholder }),
            hasPlaceholder && type !== InputType.SEARCH && React.createElement("label", null, placeholder),
            hasIcon && localStatus && !customIcon && !loading && !customIconLeft && !customIconRight && (React.createElement("div", { onClick: () => {
                    if (onIconClick) {
                        onIconClick({ inputName: name !== null && name !== void 0 ? name : '', inputValue: _value });
                    }
                } },
                React.createElement(Icon, { className: iconClassname, name: inputIcon.get(localStatus), size: IconSize.SMALL }))),
            customIcon && !localStatus && !loading && (React.createElement("div", { onClick: () => {
                    if (onIconClick) {
                        onIconClick({ inputName: name !== null && name !== void 0 ? name : '', inputValue: _value });
                    }
                } },
                React.createElement(Icon, { className: iconClassname, name: customIcon, size: IconSize.SMALL }))),
            customIconLeft && !loading && (React.createElement("div", { onClick: () => {
                    if (onIconClick) {
                        onIconClick({ inputName: name !== null && name !== void 0 ? name : '', inputValue: _value });
                    }
                } },
                React.createElement(Icon, { className: clsx(customIconLeft && 'icon-left', iconClassname), name: customIconLeft, size: IconSize.SMALL }))),
            customIconRight && !loading && type !== 'password' && (React.createElement("div", { onClick: () => {
                    if (onIconClick) {
                        onIconClick({ inputName: name !== null && name !== void 0 ? name : '', inputValue: _value });
                    }
                } },
                React.createElement(Icon, { className: clsx(customIconRight && 'icon-right', iconClassname), name: customIconRight, size: IconSize.SMALL }))),
            !loading && type === 'password' && (React.createElement("div", { "data-show-pwd": true, onClick: () => {
                    if (inputType === 'password') {
                        setInputType('text');
                        setIsShowPwd(true);
                    }
                    else {
                        setInputType('password');
                        setIsShowPwd(false);
                    }
                    if (onIconClick) {
                        onIconClick({ inputName: name !== null && name !== void 0 ? name : '', inputValue: _value });
                    }
                } },
                React.createElement(Icon, { className: clsx('icon-right', iconClassname), name: isShowPwd ? 'tri-eye-slash' : 'tri-eye', size: IconSize.SMALL }))),
            customIcon && localStatus && !loading && (React.createElement("div", { onClick: () => {
                    if (onIconClick) {
                        onIconClick({ inputName: name !== null && name !== void 0 ? name : '', inputValue: _value });
                    }
                } },
                React.createElement(Icon, { className: iconClassname, name: customIcon, size: IconSize.SMALL }))),
            type === InputType.SEARCH && !customIcon && localStatus === 'default' && !loading && (React.createElement(React.Fragment, null,
                React.createElement("div", { onClick: () => {
                        if (onIconClick) {
                            onIconClick({ inputName: name !== null && name !== void 0 ? name : '', inputValue: _value });
                        }
                    } },
                    React.createElement(Icon, { className: iconClassname, name: IconName.SEARCH, size: IconSize.SMALL }),
                    _value && _value.length > 0 && (React.createElement(Icon, { onClick: () => setValue(''), className: hashClass(styled, clsx(is('justified-self'))), name: IconName.TIMES_CIRCLE, size: IconSize.SMALL }))))),
            loading && React.createElement("span", { className: hashClass(styled, clsx(is('searching'))) })),
        help && React.createElement(Text, { className: helpClasses }, help),
        securityGauge && type === 'password' && (React.createElement("div", { "data-testid": 'security-gauge', className: hashClass(styled, clsx('security-gauge-container')) },
            React.createElement("div", { className: hashClass(styled, clsx('security-gauge')) },
                React.createElement("div", { "data-gauge": true, style: { width: widthGauge, backgroundColor: colorGauge }, className: hashClass(styled, clsx('gauge')) })),
            React.createElement("div", { className: hashClass(styled, clsx('security-gauge-verifies')) },
                React.createElement("div", null,
                    dataVerifies && dataVerifies.length && (React.createElement("div", { "data-security-length": true, className: hashClass(styled, clsx('security security-length')) },
                        React.createElement("span", { "data-icon-securities": true, className: iconClasse },
                            React.createElement("i", { ref: refLengthVerify, className: iconTimesClasse, "data-length-min": dataVerifies.length.min, "data-length-max": dataVerifies.length.max })),
                        React.createElement("span", null,
                            dataVerifies.length.min &&
                                dataVerifies.length.max &&
                                `Entre ${dataVerifies.length.min} et ${dataVerifies.length.max} caractères`,
                            dataVerifies.length.min &&
                                !dataVerifies.length.max &&
                                `Minimum ${dataVerifies.length.min} caractères`,
                            dataVerifies.length.max &&
                                !dataVerifies.length.min &&
                                `Maximum ${dataVerifies.length.max} caractères`))),
                    dataVerifies && dataVerifies.specialChars && (React.createElement("div", { "data-security-special-chars": true, className: hashClass(styled, clsx('security security-special-chars')) },
                        React.createElement("span", { "data-icon-securities": true, className: iconClasse },
                            React.createElement("i", { ref: refSpecialCharsVerify, className: iconTimesClasse })),
                        React.createElement("span", null, "Caract\u00E8res sp\u00E9ciaux"))),
                    dataVerifies && dataVerifies.number && (React.createElement("div", { "data-security-number": true, className: hashClass(styled, clsx('security security-number')) },
                        React.createElement("span", { "data-icon-securities": true, className: iconClasse },
                            React.createElement("i", { ref: refNumberVerify, className: iconTimesClasse })),
                        React.createElement("span", null, "Chiffre")))),
                React.createElement("div", null,
                    dataVerifies && dataVerifies.uppercase && (React.createElement("div", { "data-security-uppercase": true, className: hashClass(styled, clsx('security security-uppercase')) },
                        React.createElement("span", { "data-icon-securities": true, className: iconClasse },
                            React.createElement("i", { ref: refUppercaseVerify, className: iconTimesClasse })),
                        React.createElement("span", null, "Majuscule"))),
                    dataVerifies && dataVerifies.lowercase && (React.createElement("div", { "data-security-lowercase": true, className: hashClass(styled, clsx('security security-lowercase')) },
                        React.createElement("span", { "data-icon-securities": true, className: iconClasse },
                            React.createElement("i", { ref: refLowerercaseVerify, className: iconTimesClasse })),
                        React.createElement("span", null, "Minuscule")))))))));
};
export default Input;
//# sourceMappingURL=Input.js.map